import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';

export interface Language {
  code: string;
  label: string;
}

export interface LanguageSelectorProps {
  languages: Language[];
  currentLanguage: string;
  onLanguageChange: (languageCode: string) => void;
  className?: string;
  /** Accessible name for the trigger. Pass a translated string from the app. */
  ariaLabel?: string;
  /** Show the current language name next to the globe on small screens too. */
  alwaysShowLabel?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  languages,
  currentLanguage,
  onLanguageChange,
  className = '',
  ariaLabel = 'Select language',
  alwaysShowLabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((lang) => lang.code === currentLanguage);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      className={`relative ${className}`}
      ref={menuRef}
      data-id="language-selector"
    >
      {/* Language button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <Globe className="w-5 h-5 text-foreground-secondary" />
        <span
          className={`${
            alwaysShowLabel ? 'block' : 'hidden sm:block'
          } text-sm font-medium text-foreground-secondary`}
        >
          {currentLang?.label || currentLanguage}
        </span>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => {
                onLanguageChange(language.code);
                setIsOpen(false);
              }}
              className="w-full flex items-center justify-between px-4 py-2 text-sm text-foreground-secondary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span>{language.label}</span>
              {language.code === currentLanguage && (
                <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
